import React, { useState } from 'react';
import {
  Activity,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Download,
  Filter,
  Info,
  MapPin,
  MoreHorizontal,
  MousePointer2,
  PenTool,
  QrCode,
  RotateCw,
  Search,
  Type,
  X,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { PDFViewerState } from '../../types';

interface PDFToolbarProps {
  viewerState: PDFViewerState;
  fileName: string;
  fileOptions: Array<{
    id: string;
    label: string;
  }>;
  selectedFileId: string | null;
  onFileSelect: (option: {
    id: string;
    label: string;
  }) => void;
  onRotate: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onClose: () => void;
  onDownload: () => void;
  revisionOptions: Array<{
    id: string;
    label: string;
    pdf_file_id?: string;
    pdf_page_number?: number;
    sheet_revision_id?: string;
    is_current?: boolean;
  }>;
  selectedRevisionId: string | null;
  onRevisionSelect: (option: {
    id: string;
    label: string;
    pdf_file_id?: string;
    pdf_page_number?: number;
    sheet_revision_id?: string;
    is_current?: boolean;
  }) => void;
  onPublishRevision?: (option: {
    id: string;
    label: string;
    pdf_file_id?: string;
    pdf_page_number?: number;
    sheet_revision_id?: string;
    is_current?: boolean;
  }) => void;
  activeMarkupTool: 'pan' | 'shape';
  onMarkupToolChange: (tool: 'pan' | 'shape') => void;
  selectedShapeType: 'select' | 'cloud' | 'arrow' | 'textbox' | 'pin';
  onSelectedShapeTypeChange: (shape: 'select' | 'cloud' | 'arrow' | 'textbox' | 'pin') => void;
  markupScopeFilter: 'all' | 'personal' | 'shared';
  onMarkupScopeFilterChange: (filter: 'all' | 'personal' | 'shared') => void;
  onToggleActivity: () => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onSearchNext: () => void;
  onSearchPrevious: () => void;
  onSearchEnterNext: () => void;
  onSearchEnterPrevious: () => void;
  searchResultCount: number;
  activeSearchResultIndex: number;
  isSearchLoading?: boolean;
}

export const PDFToolbar: React.FC<PDFToolbarProps> = ({
  viewerState,
  fileName,
  fileOptions,
  selectedFileId,
  onFileSelect,
  onRotate,
  onPreviousPage,
  onNextPage,
  onClose,
  onDownload,
  revisionOptions,
  selectedRevisionId,
  onRevisionSelect,
  onPublishRevision,
  activeMarkupTool,
  onMarkupToolChange,
  selectedShapeType,
  onSelectedShapeTypeChange,
  markupScopeFilter,
  onMarkupScopeFilterChange,
  onToggleActivity,
  searchQuery,
  onSearchQueryChange,
  onSearchNext,
  onSearchPrevious,
  onSearchEnterNext,
  onSearchEnterPrevious,
  searchResultCount,
  activeSearchResultIndex,
  isSearchLoading = false,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false);
  const [isShapesMenuOpen, setIsShapesMenuOpen] = useState(false);
  const [isRevisionMenuOpen, setIsRevisionMenuOpen] = useState(false);
  const { currentPage, numPages } = viewerState;

  return (
    <div
      className="relative border-b border-border bg-card shadow-sm"
      data-tour-id="drawings-editor-toolbar"
    >
      <div className="space-y-2 px-3 py-2 sm:px-4">
        {/* Row 1: File + Revision + Paging */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
          <Popover open={isFileMenuOpen} onOpenChange={setIsFileMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="max-w-[220px] flex-1 justify-between sm:max-w-[280px]"
                data-tour-id="drawings-file-picker"
              >
                <span className="truncate text-sm">{fileName}</span>
                <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="bottom" className="w-80 p-1">
              <div className="max-h-64 overflow-auto">
                {fileOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">No drawings available</div>
                ) : (
                  fileOptions.map((option) => (
                    <Button
                      key={option.id}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-xs"
                      onClick={() => {
                        onFileSelect(option);
                        setIsFileMenuOpen(false);
                      }}
                    >
                      <Check className={`mr-1 h-3.5 w-3.5 ${option.id === selectedFileId ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="truncate">{option.label}</span>
                    </Button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
          <Popover open={isRevisionMenuOpen} onOpenChange={setIsRevisionMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-w-[110px] justify-between sm:min-w-[140px]"
                data-tour-id="drawings-revision-picker"
              >
                <span className="max-w-[110px] truncate text-sm sm:max-w-[160px]">
                  {revisionOptions.find((option) => option.id === selectedRevisionId)?.label ?? 'Revision'}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="bottom" className="w-56 p-1">
              <div className="max-h-64 overflow-auto">
                {revisionOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">No revisions available</div>
                ) : (
                  revisionOptions.map((option) => (
                    <div key={option.id} className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => {
                          onRevisionSelect(option);
                          setIsRevisionMenuOpen(false);
                        }}
                      >
                        <Check className={`mr-1 h-3.5 w-3.5 ${option.id === selectedRevisionId ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="truncate">{option.label}</span>
                      </Button>
                      {option.sheet_revision_id && !option.is_current && onPublishRevision && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[10px]"
                          onClick={() => onPublishRevision(option)}
                        >
                          Publish
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
          </div>

          <div className="ml-auto flex items-center space-x-2" data-tour-id="drawings-page-nav">
            <Button variant="outline" size="sm" onClick={onPreviousPage} disabled={currentPage <= 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center space-x-2 rounded-md bg-muted px-3 py-1">
              <span className="text-sm font-medium text-foreground">{currentPage}</span>
              <span className="text-sm text-muted-foreground">/</span>
              <span className="text-sm text-muted-foreground">{numPages || 1}</span>
            </div>
            <Button variant="outline" size="sm" onClick={onNextPage} disabled={numPages <= 0 || currentPage >= numPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Row 2: Primary tools */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRotate}>
            <RotateCw className="h-4 w-4" />
          </Button>
          <Popover open={isShapesMenuOpen} onOpenChange={setIsShapesMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={activeMarkupTool === 'shape' ? 'bg-accent text-foreground' : 'text-muted-foreground'}
                onClick={() => onMarkupToolChange('shape')}
                data-tour-id="drawings-shapes"
              >
                <PenTool className="mr-1 h-4 w-4" />
                Shapes
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="bottom"
              className="w-auto p-2"
              onInteractOutside={(event) => event.preventDefault()}
            >
              <div className="flex items-start gap-1">
                <Button
                  size="sm"
                  variant={selectedShapeType === 'select' ? 'default' : 'ghost'}
                  onClick={() => {
                    onSelectedShapeTypeChange('select');
                    onMarkupToolChange('shape');
                  }}
                  className="h-auto min-w-[52px] flex-col gap-1 px-2 py-1.5"
                >
                  <MousePointer2 className="h-4 w-4" />
                  <span className="text-[10px] leading-none font-normal">Select</span>
                </Button>
                <Button
                  size="sm"
                  variant={selectedShapeType === 'cloud' ? 'default' : 'ghost'}
                  onClick={() => {
                    onSelectedShapeTypeChange('cloud');
                    onMarkupToolChange('shape');
                  }}
                  className="h-auto min-w-[52px] flex-col gap-1 px-2 py-1.5"
                >
                  <Cloud className="h-4 w-4" />
                  <span className="text-[10px] leading-none font-normal">Cloud</span>
                </Button>
                <Button
                  size="sm"
                  variant={selectedShapeType === 'arrow' ? 'default' : 'ghost'}
                  onClick={() => {
                    onSelectedShapeTypeChange('arrow');
                    onMarkupToolChange('shape');
                  }}
                  className="h-auto min-w-[52px] flex-col gap-1 px-2 py-1.5"
                >
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-[10px] leading-none font-normal">Arrow</span>
                </Button>
                <Button
                  size="sm"
                  variant={selectedShapeType === 'textbox' ? 'default' : 'ghost'}
                  onClick={() => {
                    onSelectedShapeTypeChange('textbox');
                    onMarkupToolChange('shape');
                  }}
                  className="h-auto min-w-[52px] flex-col gap-1 px-2 py-1.5"
                >
                  <Type className="h-4 w-4" />
                  <span className="text-[10px] leading-none font-normal">Text</span>
                </Button>
                <Button
                  size="sm"
                  variant={selectedShapeType === 'pin' ? 'default' : 'ghost'}
                  onClick={() => {
                    onSelectedShapeTypeChange('pin');
                    onMarkupToolChange('shape');
                  }}
                  className="h-auto min-w-[52px] flex-col gap-1 px-2 py-1.5"
                >
                  <MapPin className="h-4 w-4" />
                  <span className="text-[10px] leading-none font-normal">Pin</span>
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => {
              const order: Array<'all' | 'personal' | 'shared'> = ['all', 'personal', 'shared'];
              const idx = order.indexOf(markupScopeFilter);
              onMarkupScopeFilterChange(order[(idx + 1) % order.length]);
            }}
            data-tour-id="drawings-markup-filter"
          >
            <Filter className="mr-1 h-4 w-4" />
            <span className="hidden sm:inline">{`Filter: ${markupScopeFilter}`}</span>
            <span className="sm:hidden">{markupScopeFilter}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={onToggleActivity}
            data-tour-id="drawings-activity"
          >
            <Activity className="mr-1 h-4 w-4" />
            <span className="hidden sm:inline">Activity</span>
          </Button>

          <div className="ml-auto hidden items-center gap-1 md:flex">
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onDownload}>
              <Download className="mr-1 h-4 w-4" />
              Download
            </Button>
            <Button variant="ghost" size="sm" className="text-muted-foreground">
              <QrCode className="mr-1 h-4 w-4" />
              QR Code
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHelp((prev) => !prev)}
              className={showHelp ? 'bg-accent text-foreground' : 'text-muted-foreground'}
              aria-label="Info"
              title="Info"
            >
              <Info className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              data-tour-id="drawings-close"
            >
              <X className="mr-1 h-4 w-4" />
              Close
            </Button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="ml-auto text-muted-foreground md:hidden">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onDownload}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </DropdownMenuItem>
              <DropdownMenuItem>
                <QrCode className="mr-2 h-4 w-4" />
                QR Code
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowHelp((prev) => !prev)}>
                <Info className="mr-2 h-4 w-4" />
                Info
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onClose}>
                <X className="mr-2 h-4 w-4" />
                Close
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Row 3: Search (full-width to avoid crowding) */}
        <div
          className="flex items-center gap-1 rounded-md border border-border px-2 py-1"
          data-tour-id="drawings-search-text"
        >
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (event.shiftKey) {
                  onSearchEnterPrevious();
                  return;
                }
                onSearchEnterNext();
              }
            }}
            placeholder="Search text"
            className="h-7 min-w-0 flex-1 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
          />
          <span className="min-w-[56px] text-center text-[10px] text-muted-foreground">
            {searchQuery.trim().length === 0
              ? '-'
              : isSearchLoading
                ? '...'
                : searchResultCount > 0
                  ? `${activeSearchResultIndex}/${searchResultCount}`
                  : '0/0'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground"
            disabled={searchResultCount <= 0}
            onClick={onSearchPrevious}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground"
            disabled={searchResultCount <= 0}
            onClick={onSearchNext}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {showHelp && (
        <div className="absolute right-4 top-full mt-2 bg-popover text-popover-foreground border border-border text-xs rounded-lg p-3 shadow-lg z-50 w-72">
          <div className="space-y-1">
            <div className="font-semibold mb-2">Controls</div>
            <div>Scroll / trackpad: zoom at cursor</div>
            <div>iPad two-finger pinch: zoom at finger midpoint</div>
            <div>Left-click drag: pan</div>
            <div>Use search Enter to jump/zoom to results</div>
          </div>
        </div>
      )}
    </div>
  );
};
